import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonCheckbox,
  IonContent,
  IonHeader,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonSelect,
  IonSelectOption,
} from "@ionic/react";
import { useHistory, useLocation } from "react-router-dom";
import { DataContext } from "../context/DataContext";
import Toolbar from "../components/Toolbar";

const DAUERBESTELLUNG_KEINE_AENDERUNG = "-1";
const ZAHLUNGSART_OPTIONS = [
  { value: "0", label: "Überweisung" },
  { value: "1", label: "Lastschrift manuell" },
  { value: "2", label: "Lastschrift automatisch" },
];
const BANK_KONTOINHABER = "KBK Kunterbunte Kueche gGmbH";
const BANK_IBAN = "DE88 2005 0550 1002 1888 68";
const BANK_BIC = "HASPDEHHXXX";
const BANK_GLAEUBIGER_ID = "DE96HSG00001300757";
const VERWENDUNGSZWECK = "Neues Schuelerkonto";

type SchoolOption = { value: string; label: string };

const normalizeString = (value: unknown) => String(value ?? "").trim();

const parseSchoolsAndClasses = (rawData: any): {
  schools: SchoolOption[];
  classMap: Record<string, string[]>;
} => {
  const normalizedData =
    rawData &&
    typeof rawData === "object" &&
    !Array.isArray(rawData) &&
    Array.isArray((rawData as any).data)
      ? (rawData as any).data
      : rawData;

  const classSetBySchool = new Map<string, Set<string>>();
  const schoolLabelById = new Map<string, string>();

  const addClass = (schoolIdRaw: unknown, classNameRaw: unknown, schoolLabelRaw?: unknown) => {
    const schoolId = normalizeString(schoolIdRaw);
    const className = normalizeString(classNameRaw);
    const schoolLabel = normalizeString(schoolLabelRaw);

    if (!schoolId || !className) {
      return;
    }

    if (!classSetBySchool.has(schoolId)) {
      classSetBySchool.set(schoolId, new Set<string>());
    }
    classSetBySchool.get(schoolId)?.add(className);

    if (schoolLabel && !schoolLabelById.has(schoolId)) {
      schoolLabelById.set(schoolId, schoolLabel);
    }
  };

  const parseObjectSchoolEntry = (schoolIdRaw: unknown, entry: any) => {
    const schoolId = normalizeString(schoolIdRaw);
    if (!schoolId || !entry) {
      return;
    }

    const schoolLabel =
      entry?.label ??
      entry?.schoolLabel ??
      entry?.schoolName ??
      entry?.name ??
      entry?.campusmenu_klassen_betriebe_name;

    const classes =
      entry?.classes ??
      entry?.klassen ??
      entry?.data ??
      [];

    if (Array.isArray(classes)) {
      classes.forEach((className: unknown) => addClass(schoolId, className, schoolLabel));
    }
  };

  if (Array.isArray(normalizedData)) {
    normalizedData.forEach((entry: any) => {
      if (!entry) {
        return;
      }

      const schoolId =
        entry?.campusmenu_klassen_betriebe_id ??
        entry?.betriebe_id ??
        entry?.schoolId ??
        entry?.school_id ??
        entry?.value ??
        entry?.id;

      const schoolLabel =
        entry?.campusmenu_klassen_betriebe_name ??
        entry?.betriebe_name ??
        entry?.schoolLabel ??
        entry?.schoolName ??
        entry?.school_name ??
        entry?.label ??
        entry?.name;

      const singleClassName =
        entry?.campusmenu_klassen_name ??
        entry?.klasse ??
        entry?.className;

      if (singleClassName !== undefined) {
        addClass(schoolId, singleClassName, schoolLabel);
      }

      const classList = entry?.classes ?? entry?.klassen;
      if (Array.isArray(classList)) {
        classList.forEach((className: unknown) => addClass(schoolId, className, schoolLabel));
      }
    });
  } else if (normalizedData && typeof normalizedData === "object") {
    Object.entries(normalizedData).forEach(([schoolId, entry]) => {
      if (Array.isArray(entry)) {
        entry.forEach((className) => addClass(schoolId, className));
      } else {
        parseObjectSchoolEntry(schoolId, entry);
      }
    });
  }

  const classMap: Record<string, string[]> = {};
  const schools: SchoolOption[] = [];

  classSetBySchool.forEach((classSet, schoolId) => {
    classMap[schoolId] = Array.from(classSet);
    schools.push({
      value: schoolId,
      label: schoolLabelById.get(schoolId) ?? `Schule ${schoolId}`,
    });
  });

  schools.sort((a, b) => a.label.localeCompare(b.label, "de"));
  return { schools, classMap };
};

const StudentData: React.FC = () => {
  const history = useHistory();
  const location = useLocation<{ lockOrderTypeToChild?: boolean }>();
  const lockOrderTypeToChild = Boolean(location.state?.lockOrderTypeToChild);
  const { appToken, appLoggedInChange, appUserData } = useContext(DataContext as any) as {
    appToken?: string;
    appLoggedInChange?: (isLoggedIn: boolean) => void;
    appUserData?: any;
  };
  const [orderType, setOrderType] = useState<string>("child");
  const [schoolEntryType, setSchoolEntryType] = useState<string>("new-2026-27");
  const [isTeacherOrEmployee, setIsTeacherOrEmployee] = useState<boolean>(false);
  const [isZuschuss, setIsZuschuss] = useState<boolean>(false);
  const [regularMealParticipation, setRegularMealParticipation] = useState<string>("");
  const [zahlungsart, setZahlungsart] = useState<string>("");
  const [iban, setIban] = useState<string>("");
  const [aufladebetrag, setAufladebetrag] = useState<string>("95,00");
  const [dauerbestellung1, setDauerbestellung1] = useState<string>("0");
  const [dauerbestellung2, setDauerbestellung2] = useState<string>("0");
  const [dauerbestellung3, setDauerbestellung3] = useState<string>("0");
  const [dauerbestellung4, setDauerbestellung4] = useState<string>("0");
  const [dauerbestellung5, setDauerbestellung5] = useState<string>("0");
  const [schule, setSchule] = useState<string>("");
  const [klasse, setKlasse] = useState<string>("");
  const [vorname, setVorname] = useState<string>("");
  const [familienname, setFamilienname] = useState<string>("");
  const [geburtsdatum, setGeburtsdatum] = useState<string>("");
  const [schoolOptions, setSchoolOptions] = useState<SchoolOption[]>([]);
  const [classOptionsBySchool, setClassOptionsBySchool] = useState<Record<string, string[]>>({});
  const [isLoadingSchools, setIsLoadingSchools] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const classOptions = useMemo(() => classOptionsBySchool[schule] || [], [classOptionsBySchool, schule]);

  useEffect(() => {
    if (lockOrderTypeToChild && orderType !== "child") {
      setOrderType("child");
    }
  }, [lockOrderTypeToChild, orderType]);

  useEffect(() => {
    let isMounted = true;

    const loadSchoolsAndClasses = async () => {
      if (!appToken) {
        if (isMounted) {
          setSchoolOptions([]);
          setClassOptionsBySchool({});
          setIsLoadingSchools(false);
        }
        return;
      }

      setIsLoadingSchools(true);

      try {
        const formData = new FormData();
        formData.append("appToken", appToken);

        const response = await fetch(
          "https://login.saint-online.de/funktionen/api2/campusmenu/getKlassen.php",
          { method: "POST", body: formData }
        );

        const raw = await response.text();
        const parsed = raw ? JSON.parse(raw) : null;
        const parsedData = parseSchoolsAndClasses(parsed?.data);

        if (!isMounted) {
          return;
        }

        setSchoolOptions(parsedData.schools);
        setClassOptionsBySchool(parsedData.classMap);
      } catch (error) {
        if (isMounted) {
          setSchoolOptions([]);
          setClassOptionsBySchool({});
          alert("Schulen und Klassen konnten nicht geladen werden.");
        }
      } finally {
        if (isMounted) {
          setIsLoadingSchools(false);
        }
      }
    };

    loadSchoolsAndClasses();

    return () => {
      isMounted = false;
    };
  }, [appToken]);

  useEffect(() => {
    if (schule && !classOptionsBySchool[schule]) {
      setSchule("");
      setKlasse("");
      return;
    }

    if (klasse && !classOptionsBySchool[schule]?.includes(klasse)) {
      setKlasse("");
    }
  }, [classOptionsBySchool, klasse, schule]);

  const normalisiereAufladebetragEingabe = (value: string) => {
    const nurErlaubteZeichen = value.replace(/[^0-9,.]/g, "");
    const mitKomma = nurErlaubteZeichen.replace(/\./g, ",");
    const teile = mitKomma.split(",");
    const ganzzahlTeil = (teile[0] || "").replace(/^0+(?=\d)/, "");
    const nachkommaTeil = teile.slice(1).join("").slice(0, 2);

    if (nachkommaTeil) {
      return `${ganzzahlTeil || "0"},${nachkommaTeil}`;
    }

    if (mitKomma.startsWith(",")) {
      return "0,";
    }

    return ganzzahlTeil;
  };

  const normalisiereIban = (value: string) => value.toUpperCase().replace(/[^A-Z0-9]/g, "");

  const formatiereIbanAnzeige = (value: string) => {
    const normalized = normalisiereIban(value);
    return normalized.replace(/(.{4})/g, "$1 ").trim();
  };

  const pruefeIban = (value: string) => {
    const normalized = normalisiereIban(value);

    if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/.test(normalized)) {
      return false;
    }

    const rearranged = normalized.slice(4) + normalized.slice(0, 4);
    let remainder = 0;

    for (const char of rearranged) {
      const expanded = /[A-Z]/.test(char) ? String(char.charCodeAt(0) - 55) : char;
      for (const digit of expanded) {
        remainder = (remainder * 10 + Number(digit)) % 97;
      }
    }

    return remainder === 1;
  };

  const aufladebetragFuerApi = (value: string) => {
    const normalisiert = normalisiereAufladebetragEingabe(value);
    if (!normalisiert) {
      return "";
    }

    const numerisch = Number.parseFloat(normalisiert.replace(",", "."));
    if (Number.isNaN(numerisch)) {
      return "";
    }

    return numerisch.toFixed(2);
  };

  const formatiereAufladebetragAnzeige = (value: string) => {
    const apiWert = aufladebetragFuerApi(value);
    if (!apiWert) {
      return "";
    }

    return apiWert.replace(".", ",");
  };

  const requiresAufladebetrag = zahlungsart === "2";
  const requiresIban = zahlungsart === "1" || zahlungsart === "2";
  const hasValidIban = !requiresIban || pruefeIban(iban);
  const hasValidAufladebetrag = !requiresAufladebetrag || aufladebetragFuerApi(aufladebetrag) !== "";
  const requiresDauerbestellung = regularMealParticipation === "yes";
  const hasValidDauerbestellung =
    !requiresDauerbestellung ||
    (dauerbestellung1 !== "" &&
      dauerbestellung2 !== "" &&
      dauerbestellung3 !== "" &&
      dauerbestellung4 !== "" &&
      dauerbestellung5 !== "");

  const isFormComplete =
    orderType !== "" &&
    schoolEntryType !== "" &&
    (orderType === "self" ||
      (schule.trim() !== "" &&
        klasse.trim() !== "" &&
        vorname.trim() !== "" &&
        familienname.trim() !== "" &&
        geburtsdatum !== "" &&
        regularMealParticipation !== "" &&
        hasValidDauerbestellung)) &&
    zahlungsart !== "" &&
      hasValidIban &&
    hasValidAufladebetrag;

  const handleContinue = async () => {
    if (!isFormComplete) {
      alert("Bitte füllen Sie alle Felder aus.");
      return;
    }

    if (!appToken) {
      alert("Bitte erneut anmelden. Kein gueltiger App-Token vorhanden.");
      return;
    }

    if (!hasValidIban) {
      alert("Bitte geben Sie eine gueltige IBAN ein.");
      return;
    }

    const userVorname = String(
      appUserData?.campusmenu_user_vorname ??
      appUserData?.vorname ??
      ""
    ).trim();
    const userNachname = String(
      appUserData?.campusmenu_user_nachname ??
      appUserData?.nachname ??
      ""
    ).trim();
    const schuelerVorname = orderType === "self" ? userVorname : vorname;
    const schuelerNachname = orderType === "self" ? userNachname : familienname;

    const schuelerPayload: Record<string, string> = {
      campusmenu_schueler_vorname: schuelerVorname,
      campusmenu_schueler_nachname: schuelerNachname,
      campusmenu_schueler_klasse: klasse,
      campusmenu_schueler_schule: schule,
      campusmenu_schueler_schule_id: schule,
      campusmenu_schueler_geb_datum: geburtsdatum,
      campusmenu_schueler_zahlung: zahlungsart,
      campusmenu_schueler_iban: normalisiereIban(iban),
      campusmenu_schueler_aufladebetrag: aufladebetragFuerApi(String(aufladebetrag || "")),
      campusmenu_schueler_verwendungszweck: VERWENDUNGSZWECK,
      campusmenu_schueler_db_1: dauerbestellung1,
      campusmenu_schueler_db_2: dauerbestellung2,
      campusmenu_schueler_db_3: dauerbestellung3,
      campusmenu_schueler_db_4: dauerbestellung4,
      campusmenu_schueler_db_5: dauerbestellung5,
      campusmenu_schueler_bestellart: orderType,
      campusmenu_schueler_anmeldung_schuljahr: schoolEntryType,
      campusmenu_schueler_teilnahme_regelmaessig: regularMealParticipation,
      campusmenu_schueler_zuschuss: isZuschuss ? "1" : "0",
      campusmenu_schueler_lehrer_angestellter: isTeacherOrEmployee ? "1" : "0",
    };

    const formData = new FormData();
    formData.append("appToken", appToken);

    Object.entries(schuelerPayload).forEach(([key, value]) => {
      formData.append(key, value ?? "");
    });

    setIsSubmitting(true);

    try {
      const response = await fetch(
        "https://login.saint-online.de/funktionen/api2/campusmenu/createSchueler.php",
        { method: "POST", body: formData }
      );

      const raw = await response.text();
      let data: any = null;

      try {
        data = raw ? JSON.parse(raw) : null;
      } catch {
        alert(raw || `Serverfehler (${response.status})`);
        return;
      }

      if (data?.status == 200) {
        alert("Erfolgreich angelegt. Bitte loggen Sie sich erneut ein um die Änderung anzuzeigen. Vielen Dank.");
          if (typeof appLoggedInChange === "function") {
            appLoggedInChange(false);
          }
          history.push("/login");
      } else {
        alert(JSON.stringify(data));
      }
    } catch (error: any) {
      alert(JSON.stringify(error?.msg ?? error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <IonPage>
      <IonContent fullscreen>
        <IonHeader>
          <Toolbar />
        </IonHeader>
        <IonCard>
          <IonCardHeader>
            <IonCardTitle>Daten des Essensteilnehmers</IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <IonItem>
              <IonLabel position="floating">Bestellart</IonLabel>
              <IonSelect
                value={orderType}
                onIonChange={(e: any) => setOrderType(String(e.detail.value ?? "child"))}
                disabled={lockOrderTypeToChild}
              >
                <IonSelectOption value="child">Ich bestelle für mein Kind</IonSelectOption>
                <IonSelectOption value="self">Ich bestelle für mich selbst</IonSelectOption>
              </IonSelect>
            </IonItem>
            <IonItem>
              <IonLabel position="floating">Anmeldung für welches Schuljahr</IonLabel>
              <IonSelect value={schoolEntryType} onIonChange={(e: any) => setSchoolEntryType(String(e.detail.value ?? ""))}>
                <IonSelectOption value="2026">Neuanmeldung ab Schuljahr 2026/27</IonSelectOption>
                <IonSelectOption value="2025">Anmeldung für das laufende Schuljahr (2025/26)</IonSelectOption>
              </IonSelect>
            </IonItem>
            {orderType === "child" ? (
              <>
                <p>Bitte geben Sie die Daten des Schülers ein.</p>
                <IonItem>
                  <IonLabel position="floating">Schule</IonLabel>
                  <IonSelect
                    value={schule}
                    onIonChange={(e: any) => {
                      const selectedSchool = String(e.detail.value ?? "");
                      setSchule(selectedSchool);
                      if (!classOptionsBySchool[selectedSchool]?.includes(klasse)) {
                        setKlasse("");
                      }
                    }}
                    disabled={isLoadingSchools || schoolOptions.length === 0}
                  >
                    {schoolOptions.map((school) => (
                      <IonSelectOption key={school.value} value={school.value}>
                        {school.label}
                      </IonSelectOption>
                    ))}
                  </IonSelect>
                </IonItem>
                <IonItem>
                  <IonLabel position="floating">Klasse</IonLabel>
                  <IonSelect value={klasse} onIonChange={(e: any) => setKlasse(String(e.detail.value ?? ""))} disabled={classOptions.length === 0}>
                    {classOptions.map((className) => (
                      <IonSelectOption key={className} value={className}>
                        {className}
                      </IonSelectOption>
                    ))}
                  </IonSelect>
                </IonItem>
                
                <IonItem>
                  <IonLabel position="floating">Vorname</IonLabel>
                  <IonInput
                    required
                    type="text"
                    value={vorname}
                    onIonInput={(e: any) => setVorname(String(e.detail.value ?? ""))}
                  ></IonInput>
                </IonItem>
                <IonItem>
                  <IonLabel position="floating">Familienname</IonLabel>
                  <IonInput
                    required
                    type="text"
                    value={familienname}
                    onIonInput={(e: any) => setFamilienname(String(e.detail.value ?? ""))}
                  ></IonInput>
                </IonItem>
                <IonItem>
                  <IonLabel position="floating">Geburtsdatum</IonLabel>
                  <IonInput
                    required
                    type="date"
                    value={geburtsdatum}
                    onIonInput={(e: any) => setGeburtsdatum(String(e.detail.value ?? ""))}
                  ></IonInput>
                </IonItem>
                <IonItem>
                  <IonLabel position="floating">Dauerbestellung</IonLabel>
                  <IonSelect value={regularMealParticipation} onIonChange={(e: any) => setRegularMealParticipation(String(e.detail.value ?? ""))}>
                    <IonSelectOption value="yes">Mein Kind nimmt regelmäßig am Essen teil</IonSelectOption>
                    <IonSelectOption value="no">Ich bestelle jedes Essen einzeln</IonSelectOption>
                  </IonSelect>
                </IonItem>
                {regularMealParticipation === "yes" ? (
                  <IonCard>
                    <IonCardHeader>
                      <IonCardTitle>Dauerbestellung</IonCardTitle>
                      <IonCardSubtitle>Dauerbestellungen werden automatisch für jede Woche bestellt</IonCardSubtitle>
                    </IonCardHeader>
                    <IonCardContent>
                      <IonList>
                        <IonItem>
                          <IonLabel>Montag</IonLabel>
                          <IonSelect value={dauerbestellung1} onIonChange={(e:any) => setDauerbestellung1(String(e.detail.value || DAUERBESTELLUNG_KEINE_AENDERUNG))}>
                            <IonSelectOption value={DAUERBESTELLUNG_KEINE_AENDERUNG}>Keine Änderung</IonSelectOption>
                            <IonSelectOption value="0">Kein Essen</IonSelectOption>
                            <IonSelectOption value="1">Menü 1</IonSelectOption>
                            <IonSelectOption value="2">Menü 2</IonSelectOption>
                          </IonSelect>
                        </IonItem>
                        <IonItem>
                          <IonLabel>Dienstag</IonLabel>
                          <IonSelect value={dauerbestellung2} onIonChange={(e:any) => setDauerbestellung2(String(e.detail.value || DAUERBESTELLUNG_KEINE_AENDERUNG))}>
                            <IonSelectOption value="0">Kein Essen</IonSelectOption>
                            <IonSelectOption value="1">Menü 1</IonSelectOption>
                            <IonSelectOption value="2">Menü 2</IonSelectOption>
                          </IonSelect>
                        </IonItem>
                        <IonItem>
                          <IonLabel>Mittwoch</IonLabel>
                          <IonSelect value={dauerbestellung3} onIonChange={(e:any) => setDauerbestellung3(String(e.detail.value || DAUERBESTELLUNG_KEINE_AENDERUNG))}>
                            <IonSelectOption value="0">Kein Essen</IonSelectOption>
                            <IonSelectOption value="1">Menü 1</IonSelectOption>
                            <IonSelectOption value="2">Menü 2</IonSelectOption>
                          </IonSelect>
                        </IonItem>
                        <IonItem>
                          <IonLabel>Donnerstag</IonLabel>
                          <IonSelect value={dauerbestellung4} onIonChange={(e:any) => setDauerbestellung4(String(e.detail.value || DAUERBESTELLUNG_KEINE_AENDERUNG))}>
                            <IonSelectOption value="0">Kein Essen</IonSelectOption>
                            <IonSelectOption value="1">Menü 1</IonSelectOption>
                            <IonSelectOption value="2">Menü 2</IonSelectOption>
                          </IonSelect>
                        </IonItem>
                        <IonItem>
                          <IonLabel>Freitag</IonLabel>
                          <IonSelect value={dauerbestellung5} onIonChange={(e:any) => setDauerbestellung5(String(e.detail.value || DAUERBESTELLUNG_KEINE_AENDERUNG))}>
                            <IonSelectOption value="0">Kein Essen</IonSelectOption>
                            <IonSelectOption value="1">Menü 1</IonSelectOption>
                            <IonSelectOption value="2">Menü 2</IonSelectOption>
                          </IonSelect>
                        </IonItem>
                      </IonList>
                    </IonCardContent>
                  </IonCard>
                ) : null}
              </>
            ) : <IonItem>
                <IonLabel>Ich bin Lehrer oder Angestellter.</IonLabel>
                <IonCheckbox
                  checked={isTeacherOrEmployee}
                  onIonChange={(e: any) => setIsTeacherOrEmployee(Boolean(e.detail.checked))}
                />
              </IonItem>}
            
              <IonItem>
                <IonLabel>Ich habe einen Zuschuss beantragt.</IonLabel>
                <IonCheckbox
                  checked={isZuschuss}
                  onIonChange={(e: any) => setIsZuschuss(Boolean(e.detail.checked))}
                />
              </IonItem>
            <IonItem>
              <IonLabel position="floating">Zahlungsart</IonLabel>
              <IonSelect value={zahlungsart} onIonChange={(e: any) => setZahlungsart(String(e.detail.value ?? ""))}>
                {ZAHLUNGSART_OPTIONS.map((option) => (
                  <IonSelectOption key={option.value} value={option.value}>
                    {option.label}
                  </IonSelectOption>
                ))}
              </IonSelect>
            </IonItem>
            {zahlungsart === "0" ? (
              <IonItem lines="none">
                <IonLabel>
                  Bitte überweisen Sie den offenen Betrag auf das folgende Konto: <br />
                  Kontoinhaber: {BANK_KONTOINHABER}<br />
                  IBAN: {BANK_IBAN}<br />
                  BIC: {BANK_BIC}<br />
                  Gläubiger-ID: {BANK_GLAEUBIGER_ID}<br />
                  Verwendungszweck: {VERWENDUNGSZWECK}
                  <br />
                  <br />
                  Achten Sie bitte darauf, dass das Verbuchen der Gutschrift ein paar Tage dauern kann. Sobald die
                  Gutschrift verbucht ist, kann ihr Kind das Essen erhalten. Bestellungen sind auch ohne ausreichende
                  Deckung möglich, allerdings wird die Abholung verweigert, wenn das Konto nicht gedeckt ist.
                </IonLabel>
              </IonItem>
            ) : null}
            {zahlungsart === "1" ? (
              <IonItem lines="none">
                <IonLabel>
                  Lastschrift manuell - Die Lastschriften werden beim Bestellvorgang durch &quot;Lastschrift jetzt
                  einziehen&quot; eingezogen. Das Guthaben wird sofort auf dem Schüler-Konto -unter dem Vorbehalt der
                  Wertstellung- gutgeschrieben.
                  <br />
                  <br />
                  (A) Ermächtigung zum Einzug
                  <br />
                  <span style={{ fontSize: "0.7em" }}>
                    Ich ermächtige die Kunterbunten Küche, Zahlungen von meinem Konto mittels Lastschrift einzuziehen.
                    Zugleich weise ich mein Kreditinstitut an, die von die Kunterbunten Küche auf mein Konto gezogenen
                    Lastschriften einzulösen.
                  </span>
                  <br />
                  <br />
                  (B) Hinweis zum Widerruf und zur Erstattung
                  <br />
                  <span style={{ fontSize: "0.7em" }}>
                    Ich kann innerhalb von acht Wochen, beginnend mit dem Belastungsdatum, die Erstattung des
                    belasteten Betrages verlangen. Es gelten dabei die mit meinem Kreditinstitut vereinbarten
                    Bedingungen.
                  </span>
                </IonLabel>
              </IonItem>
            ) : null}
            {zahlungsart === "2" ? (
              <>
                <IonItem lines="none">
                  <IonLabel>
                    Lastschrift automatisch - Ihr Guthaben wird automatisch um den eingestellten Aufladebetrag per
                    Lastschrift erhöht. Ist das Guthaben verbraucht, wird eine erneute Lastschrift ausgelöst.
                    <br />
                    <br />
                    (A) Ermächtigung zum Einzug
                    <br />
                    <span style={{ fontSize: "0.7em" }}>
                      Ich ermächtige die Kunterbunten Küche, Zahlungen von meinem Konto mittels Lastschrift
                      einzuziehen. Zugleich weise ich mein Kreditinstitut an, die von die Kunterbunten Küche auf mein
                      Konto gezogenen Lastschriften einzulösen.
                    </span>
                    <br />
                    <br />
                    (B) Hinweis zum Widerruf und zur Erstattung
                    <br />
                    <span style={{ fontSize: "0.7em" }}>
                      Ich kann innerhalb von acht Wochen, beginnend mit dem Belastungsdatum, die Erstattung des
                      belasteten Betrages verlangen. Es gelten dabei die mit meinem Kreditinstitut vereinbarten
                      Bedingungen.
                    </span>
                  </IonLabel>
                </IonItem>
                <IonItem>
                  <IonLabel position="stacked">Aufladebetrag</IonLabel>
                  <IonInput
                    required
                    type="text"
                    inputmode="decimal"
                    value={aufladebetrag}
                    onIonInput={(e: any) => {
                      const value = normalisiereAufladebetragEingabe(String(e.detail.value || ""));
                      setAufladebetrag(value);
                    }}
                    onIonBlur={() => {
                      setAufladebetrag((prev) => formatiereAufladebetragAnzeige(String(prev || "")));
                    }}
                  />
                  <IonLabel slot="end">EUR</IonLabel>
                </IonItem>
              </>
            ) : null}
            {requiresIban ? (
              <>
                <IonItem>
                  <IonLabel position="stacked">IBAN</IonLabel>
                  <IonInput
                    required
                    type="text"
                    inputmode="text"
                    value={iban}
                    placeholder="DE00 0000 0000 0000 0000 00"
                    onIonInput={(e: any) => {
                      const value = formatiereIbanAnzeige(String(e.detail.value || ""));
                      setIban(value);
                    }}
                    onIonBlur={() => {
                      setIban((prev) => formatiereIbanAnzeige(String(prev || "")));
                    }}
                  />
                </IonItem>
                {iban.trim() !== "" && !hasValidIban ? (
                  <p style={{ color: "var(--ion-color-danger)", marginTop: "6px" }}>
                    Bitte geben Sie eine gueltige IBAN ein.
                  </p>
                ) : null}
              </>
            ) : null}
            <IonButton
              expand="full"
              onClick={handleContinue}
              disabled={!isFormComplete || isSubmitting}
            >
              {isSubmitting ? "Speichert..." : "Abschließen"}
            </IonButton>
          </IonCardContent>
        </IonCard>
      </IonContent>
    </IonPage>
  );
};

export default StudentData;
